import { getObjectTag } from '../internal-utils';

/**
 * Checks whether a value is a boolean primitive or a boxed Boolean object.
 *
 * Accepts:
 * - boolean primitives: `true`, `false`
 * - boxed booleans: `new Boolean(true)`, `Object(false)`
 *
 * @param value - The value to check.
 * @returns `true` if the value is a boolean or a boxed Boolean.
 *
 * @example
 * isBoolean(true);              // true
 * isBoolean(false);             // true
 * isBoolean(new Boolean(true)); // true
 * isBoolean('true');            // false
 */
export function isBoolean(value: unknown): value is boolean {
  return (
    typeof value === 'boolean' ||
    (typeof value === 'object' && value !== null && getObjectTag(value) === '[object Boolean]')
  );
}

/**
 * Checks whether a value is a number primitive or a boxed Number object,
 * excluding `NaN`.
 *
 * Accepts:
 * - number primitives: `42`, `3.14`
 * - boxed numbers: `new Number(42)`, `Object(3.14)`
 *
 * Rejects:
 * - `NaN` (primitive or boxed)
 * - all non-number types
 *
 * @param value - The value to check.
 * @returns `true` if the value is a number (primitive or boxed), but not `NaN`.
 *
 * @example
 * isNumber(123);             // true
 * isNumber(new Number(123)); // true
 * isNumber(NaN);             // false
 * isNumber('123');           // false
 */
export function isNumber(value: unknown): value is number {
  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  if (typeof value === 'object' && value !== null && getObjectTag(value) === '[object Number]') {
    return !Number.isNaN((value as unknown as number).valueOf());
  }

  return false;
}

/**
 * Checks whether a value is a string primitive or a boxed String object.
 *
 * Accepts:
 * - string primitives: `'hello'`, `''`
 * - boxed strings: `new String('hello')`, `Object('')`
 *
 * Rejects:
 * - all non-string types
 *
 * @param value - The value to check.
 * @returns `true` if the value is a string (primitive or boxed).
 *
 * @example
 * isString('hello');             // true
 * isString(new String('hello')); // true
 * isString('');                  // true
 * isString(123);                 // false
 */
export function isString(value: unknown): value is string {
  return (
    typeof value === 'string' ||
    (typeof value === 'object' && value !== null && getObjectTag(value) === '[object String]')
  );
}

/**
 * Checks whether a value is a bigint primitive or a boxed BigInt object.
 *
 * Accepts:
 * - bigint primitives: `123n`
 * - boxed bigints: `Object(123n)`
 *
 * Rejects:
 * - all non-bigint types
 *
 * @param value - The value to check.
 * @returns `true` if the value is a bigint (primitive or boxed).
 *
 * @example
 * isBigInt(123n);         // true
 * isBigInt(Object(123n)); // true
 * isBigInt(123);          // false
 * isBigInt('123');        // false
 */
export function isBigInt(value: unknown): value is bigint {
  return (
    typeof value === 'bigint' ||
    (typeof value === 'object' && value !== null && getObjectTag(value) === '[object BigInt]')
  );
}

/**
 * Checks whether a value is a symbol primitive or a boxed Symbol object.
 *
 * Accepts:
 * - symbol primitives: `Symbol('x')`
 * - boxed symbols: `Object(Symbol('x'))`
 *
 * Rejects:
 * - all non-symbol types
 *
 * @param value - The value to check.
 * @returns `true` if the value is a symbol (primitive or boxed).
 *
 * @example
 * isSymbol(Symbol('x'));         // true
 * isSymbol(Object(Symbol('x'))); // true
 * isSymbol('x');                 // false
 */
export function isSymbol(value: unknown): value is symbol {
  return (
    typeof value === 'symbol' ||
    (typeof value === 'object' && value !== null && getObjectTag(value) === '[object Symbol]')
  );
}
